/**
 * SF-4825: a platform super admin's company list, read and set from the admin app user page.
 *
 * sf-api routes under /admin/v1/super-admin-companies (default data provider, which unwraps the
 * `{ success, data }` envelope):
 *
 *   GET  ?email=            the person's CompanyListView (admin:users:read)
 *   GET  /options?search=&limit=&offset=   companies the caller may hand out, by name, one page
 *                           at a time (admin:users:write; offset added in SF-4996)
 *   PUT  { email, allCompanies, companyIds, revision }   set the full list (admin:users:write)
 *
 * `revision` is the opaque value from the view the editor was shown. sf-api answers 409
 * CHANGED_WHILE_SAVING when the stored list no longer matches it, so a stale editor cannot
 * overwrite a colleague's change.
 *
 * "Super admin" is the web app's platform super admin (`users.type === 'super_admin'`), found by
 * the sign-in account's email. Only a super admin may call these routes; anyone else gets 403
 * NOT_SUPER_ADMIN, which is an answer ("this card is not for you"), so it is stored as data.
 *
 * Everything fails closed: an answer that is not the documented shape is treated as an error,
 * and nothing is editable unless the server says so.
 */
import { useDataProvider, useOnError } from '@refinedev/core';
import { useQuery } from '@tanstack/react-query';

export const SUPER_ADMIN_COMPANIES_URL = '/admin/v1/super-admin-companies';

export type EditMode = 'any' | 'add' | 'remove' | 'none';

export interface ListedCompany {
  id: string;
  /** null when the company no longer exists. */
  name: string | null;
}

export interface CompanyOption {
  id: string;
  name: string;
}

export interface CompanyListView {
  isSuperAdmin: boolean;
  /** true = no list, so every company. */
  allCompanies: boolean;
  companies: ListedCompany[];
  edit: { allowed: boolean; mode: EditMode; canSetAll: boolean };
  /** Opaque: which stored list this view shows. Sent back on save. */
  revision: string;
}

export interface SaveCompaniesBody {
  email: string;
  allCompanies: boolean;
  companyIds: string[];
  /** The revision of the view the edit window was opened from. */
  revision: string;
}

const MODES: readonly EditMode[] = ['any', 'add', 'remove', 'none'];

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * The sf-api CompanyListView, or null when the answer is not the documented shape. Also null when
 * the parts contradict each other (a list beside "all companies", editing allowed in mode none,
 * "set all" outside mode any), so a confused answer never unlocks editing. `allowed` false is fine
 * in any mode: sf-api keeps mode add or remove when the rules leave nothing to change (a limited
 * super admin with one company viewing themself, or a colleague who already has every company on
 * the caller's list).
 */
export function parseCompanyListView(raw: unknown): CompanyListView | null {
  if (!isObject(raw) || !isObject(raw.edit)) return null;
  const { isSuperAdmin, allCompanies, companies, edit, revision } = raw;
  const { allowed, mode, canSetAll } = edit;
  if (typeof isSuperAdmin !== 'boolean' || typeof allCompanies !== 'boolean') return null;
  if (typeof revision !== 'string' || !revision) return null;
  if (!Array.isArray(companies)) return null;
  const parsed: ListedCompany[] = [];
  for (const c of companies) {
    if (!isObject(c) || typeof c.id !== 'string' || !c.id) return null;
    if (c.name !== null && typeof c.name !== 'string') return null;
    parsed.push({ id: c.id, name: c.name });
  }
  if (typeof allowed !== 'boolean' || typeof canSetAll !== 'boolean') return null;
  if (typeof mode !== 'string' || !MODES.includes(mode as EditMode)) return null;
  // An empty list means every company, so a super admin has either "all" or at least one.
  if (isSuperAdmin && allCompanies !== (parsed.length === 0)) return null;
  if (!isSuperAdmin && (allCompanies || parsed.length > 0 || allowed)) return null;
  if (allowed && mode === 'none') return null;
  if (canSetAll && mode !== 'any') return null;
  return {
    isSuperAdmin,
    allCompanies,
    companies: parsed,
    edit: { allowed, mode: mode as EditMode, canSetAll },
    revision,
  };
}

/** The options answer, or null when any entry is not `{ id, name }` with string values. */
export function parseCompanyOptions(raw: unknown): CompanyOption[] | null {
  if (!Array.isArray(raw)) return null;
  const options: CompanyOption[] = [];
  for (const o of raw) {
    if (!isObject(o) || typeof o.id !== 'string' || !o.id || typeof o.name !== 'string') {
      return null;
    }
    options.push({ id: o.id, name: o.name });
  }
  return options;
}

interface ApiError {
  response?: { status?: number; data?: { message?: unknown; reason_code?: unknown } };
}

export const errorStatus = (error: unknown): number | undefined =>
  (error as ApiError)?.response?.status;

/** The server's plain-words message, or the fallback when there is none. */
export function errorMessage(error: unknown, fallback: string): string {
  const message = (error as ApiError)?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

const isNotSuperAdmin = (error: unknown) =>
  errorStatus(error) === 403 &&
  (error as ApiError)?.response?.data?.reason_code === 'NOT_SUPER_ADMIN';

/** Emails are matched lowercase on the server; key the cache the same way. */
export const normaliseEmail = (email: string) => email.trim().toLowerCase();

const ROOT = ['sf-api', 'super-admin-companies'] as const;
export const companyListQueryKey = (email: string) => [...ROOT, 'view', normaliseEmail(email)];
/** Cache key for the options pages; the picker adds the search. */
export const COMPANY_OPTIONS_QUERY_KEY = [...ROOT, 'options'] as const;

/** The caller is not a super admin (render nothing), or the person's list. */
export type CompanyListAnswer =
  | { kind: 'notSuperAdminCaller' }
  | { kind: 'view'; view: CompanyListView };

function useCustomRequest() {
  const getDataProvider = useDataProvider();
  return () => {
    const custom = getDataProvider().custom;
    if (!custom) throw new Error('The data provider has no custom request.');
    return custom;
  };
}

/**
 * One person's company list, keyed by their email. Never shows another person's list while this
 * one loads (Refine keeps the previous key's data by default), and never retries: a 4xx is an
 * answer, a 5xx gets the card's Retry button.
 */
export function useCompanyList(email: string) {
  const custom = useCustomRequest();
  // useCustom passes failures to the auth provider (a 401 that cannot be refreshed logs out);
  // this query does the same.
  const { mutate: checkError } = useOnError();
  const normalised = normaliseEmail(email);
  return useQuery<CompanyListAnswer>({
    queryKey: companyListQueryKey(normalised),
    queryFn: async () => {
      try {
        const { data } = await custom()({
          url: SUPER_ADMIN_COMPANIES_URL,
          method: 'get',
          query: { email: normalised },
        });
        const view = parseCompanyListView(data);
        if (!view) throw new Error('The company list answer was not in the expected shape.');
        return { kind: 'view', view };
      } catch (error) {
        if (isNotSuperAdmin(error)) return { kind: 'notSuperAdminCaller' };
        throw error;
      }
    },
    enabled: !!normalised,
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    onError: (error) => checkError(error),
  });
}

/**
 * SF-4996: one page of the companies the caller may hand out, for the infinite-scroll picker.
 * Failures reach the auth provider through the picker.
 */
export function useFetchCompanyOptionsPage() {
  const custom = useCustomRequest();
  return async ({
    search,
    offset,
    limit,
  }: {
    search: string;
    offset: number;
    limit: number;
  }): Promise<CompanyOption[]> => {
    const { data } = await custom()({
      url: `${SUPER_ADMIN_COMPANIES_URL}/options`,
      method: 'get',
      query: { ...(search ? { search } : {}), limit, offset },
    });
    const options = parseCompanyOptions(data);
    if (!options) throw new Error('The company options answer was not in the expected shape.');
    return options;
  };
}

/**
 * PUT the full desired list. Resolves with the read-back view (null if it is not the documented
 * shape); rejects with the request error so the caller can show the server's message.
 */
export function useSaveCompanies() {
  const custom = useCustomRequest();
  const { mutate: checkError } = useOnError();
  return async (body: SaveCompaniesBody): Promise<CompanyListView | null> => {
    try {
      const { data } = await custom()({
        url: SUPER_ADMIN_COMPANIES_URL,
        method: 'put',
        payload: body,
      });
      return parseCompanyListView(data);
    } catch (error) {
      checkError(error);
      throw error;
    }
  };
}
